import { useState } from 'react';
import { IoTrashOutline } from 'react-icons/io5';

import Error from '../Error';
import Loader from '../Loader';
import Pagination from '../Pagination';
import { tableBackgroundColor } from '../utils/tableBackgroundColor';
import EditGlobalNotificationModal from './modals/EditGlobalNotificationModal';

import {
	GlobalNotificationError,
	GlobalNotificationMessage,
} from '@/exceptions/global-notification/global-notification.enum';
import useDeleteGlobalNotificaiton from '@/hooks/globalNotification/useDeleteGlobalNotification';
import { useGetAllGlobalNotifications } from '@/hooks/globalNotification/useGlobalNotification';
import { IGlobalNotificationResponse } from '@/interfaces/entities/global-notification';
import { notificationService } from '@/services/notification.service';

import { formatDateForDisplay } from '@components/utils/timeFormatter.ts';

const NotificationTable = () => {
	const deleteGlobalNotification = useDeleteGlobalNotificaiton();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isNotification, setNotification] =
		useState<IGlobalNotificationResponse>();
	const [currentPage, setCurrentPage] = useState(1);
	const {
		globalNotifications,
		isLoading,
		isError,
		error,
		isPending,
		totalPages,
	} = useGetAllGlobalNotifications(currentPage);

	if (isLoading || isPending) {
		return <Loader />;
	}

	if (isError) {
		return (
			<Error
				message={
					error ? error.message : GlobalNotificationError.NOTIFICATIONS_ERROR
				}
			/>
		);
	}

	const openModal = (notification: IGlobalNotificationResponse) => {
		setNotification(notification);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const handleDelete = (id: number) => {
		notificationService.confirm(
			GlobalNotificationMessage.DELETE_MESSAGE,
			() => {
				deleteGlobalNotification.mutate(id);
			},
		);
	};

	return (
		<div className="flex items-center justify-center flex-col mb-">
			<div className="px-4 sm:px-6 lg:px-8 w-[85%] ">
				<div className="sm:flex sm:items-center">
					<div className="sm:flex-auto">
						<h1
							data-test="h1-notifications"
							className="text-base font-semibold leading-6 text-gray-900"
						>
							Notifications
						</h1>
					</div>
				</div>
				{isModalOpen && isNotification && (
					<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
						<div className="bg-white container max-w-3xl p-3 rounded-xl shadow-lg border-2 border-black content-center">
							<EditGlobalNotificationModal
								closeModal={closeModal}
								initialState={isNotification}
							/>
						</div>
					</div>
				)}
				<div className="mt-4 flow-root">
					<div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
						<div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
							<table className="min-w-full divide-y divide-gray-300 border-red-300">
								<thead>
									<tr>
										<th
											scope="col"
											className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900 sm:pl-0"
											data-test="th-id"
										>
											ID
										</th>
										<th
											scope="col"
											className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
											data-test="th-scheduleAt"
										>
											Schedule At
										</th>
										<th
											scope="col"
											className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
											data-test="th-message"
										>
											Message
										</th>
										<th
											scope="col"
											className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
											data-test="th-sent"
										>
											Sent
										</th>
										<th
											scope="col"
											className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
											data-test="th-readBy"
										>
											Read By
										</th>
										<th
											scope="col"
											className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										>
											<span className="">Actions</span>
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200">
									{globalNotifications.map(
										(notification: IGlobalNotificationResponse, index) => {
											const { total, read } =
												notification.targetNotificationUsers.reduce(
													(acc, target) => {
														acc.total += 1;
														if (target.read) {
															acc.read += 1;
														}
														return acc;
													},
													{ total: 0, read: 0 },
												);

											return (
												<tr
													key={notification.id}
													className={`${tableBackgroundColor(
														index,
													)} hover:bg-gray-200 min-h-8`}
												>
													<td className="text-center max-w-sm overflow-hidden text-ellipsis whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 w-60 truncate">
														{notification.id}
													</td>
													<td className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-40 truncate">
														{formatDateForDisplay(
															new Date(notification.scheduledAt),
														)}
													</td>
													<td className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-40 truncate">
														{notification.message}
													</td>
													<td className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-40 truncate">
														{notification.sent ? 'Yes' : 'No'}
													</td>
													<td className="text-center max-w-md overflow-hidden text-ellipsis whitespace-nowrap px-3 py-4 text-sm text-gray-500 w-40 truncate">
														{read}/{total}
													</td>
													<td className="flex justify-center items-center gap-4 whitespace-nowrap py-4  text-sm font-medium w-40 ">
														<button
															className="text-indigo-600 hover:text-indigo-900"
															onClick={() => openModal(notification)}
															data-test="edit-notification-button"
														>
															Edit
															<span className="sr-only">{notification.id}</span>
														</button>

														<IoTrashOutline
															className="cursor-pointer"
															onClick={() => handleDelete(notification.id)}
														/>
													</td>
												</tr>
											);
										},
									)}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
			<Pagination
				currentPage={currentPage}
				setCurrentPage={setCurrentPage}
				totalPages={totalPages as number}
			/>
		</div>
	);
};

export default NotificationTable;
